import OverwatchModal from "@/components/OverwatchModal";
import CurrentUserContext from "@/Contexts/CurrentUser";
import { ReportContext } from "@/Contexts/ReportContext";
import { MarkAsDeployedDocument, GetSchedulesDocument, CompanyResourceResourceTypeEnum } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import { useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useContext, useState } from "react";

interface DeployModalProps {
  handleClose: () => void;
  stateId: string;
}

const DeployModal: React.FC<DeployModalProps> = ({ handleClose, stateId }) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { selectedReport } = useContext(ReportContext);
  const [deployData, setDeployData] = useState({
    id: stateId,
    startTime: dayjs(new Date(selectedReport?.reportDate as string).setHours(8, 0)),
    endTime: dayjs(new Date(selectedReport?.reportDate as string).setHours(17, 0)),
  });

  const [markAsDeployed] = useMutation(MarkAsDeployedDocument, {
    onCompleted: () => {
      displayNotification({ text: "Delivery And Collection Item marked as deployed successfully", type: "success" });
    },
    onError: (error) => {
      displayNotification({ text: error.message, type: "error" });
    },
    refetchQueries: [
      {
        query: GetSchedulesDocument,
        variables: {
          projectId: selectedReport?.projectId,
          date: selectedReport?.reportDate,
          resourceType: CompanyResourceResourceTypeEnum.Equipment,
        },
      },
    ]
  });

  const handleSave = () => {
    markAsDeployed({
      variables: {
        id: deployData.id,
        reportDate: selectedReport?.reportDate as string,
        startTime: deployData.startTime.toISOString(),
        endTime: deployData.endTime.toISOString(),
      },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: 'DeliveryAndCollectionItemState', id: deployData.id }) });

        cache.gc();
      },
    });
    handleClose();
  };

  return (
    <OverwatchModal
      title='Delivery / Collection'
      caption='Including service attendance'
      primaryButtonText={'Save'}
      primaryButtonAction={handleSave}
      disablePrimary={!deployData.startTime || !deployData.endTime}
      secondaryButtonText='Cancel'
      secondaryButtonAction={handleClose}
      handleClose={handleClose}
      size="md"
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid alignItems="center" size={6}>
            <TimePicker
              ampm={false}
              format={dateTimePreference.time}
              label="Check In Time"
              value={deployData.startTime}
              onChange={(newValue) => setDeployData((prev) => ({ ...prev, startTime: newValue as Dayjs }))}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid alignItems="center" size={6}>
            <TimePicker
              ampm={false}
              format={dateTimePreference.time}
              label="Check Out Time"
              value={deployData.endTime}
              onChange={(newValue) => setDeployData((prev) => ({ ...prev, endTime: newValue as Dayjs }))}
              sx={{ width: "100%" }}
            />
          </Grid>
        </LocalizationProvider>
      </Grid>
    </OverwatchModal>
  );
};

export default DeployModal;
